import React from "react";
// TRANSLATION
import { useTranslation } from "react-i18next";
// ANT DESIGN COMPONENTS
import { Radio, Tooltip } from "antd";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
// COMPONENTS
import OpcUaTextField from "./OpcUaTextField";
import EnumHelpTable, { type EnumHelpRow } from "./EnumHelpTable";
// INTERFACES
import type { OpcUaIdentityForm } from "../../../../../interfaces/AdapterCdoOpcUa.interface";

/** Build a blank identity form value for a given kind, used when the radio selection changes. */
function blankIdentityForKind(kind: OpcUaIdentityForm["kind"]): OpcUaIdentityForm {
  if (kind === "username") return { kind: "username", username: "", password: "" };
  if (kind === "x509") return { kind: "x509", certificatePath: "", privateKeyPath: "" };
  return { kind: "anonymous" };
}

export default function IdentityEditor({
  identity,
  onChangeIdentity,
}: {
  identity: OpcUaIdentityForm;
  onChangeIdentity: (value: OpcUaIdentityForm) => void;
}) {
  const [t] = useTranslation("global");

  const onChangeUsernameField = (value: string, field: "username" | "password") => {
    if (identity.kind !== "username") return;
    onChangeIdentity({ ...identity, [field]: value });
  };

  const onChangeX509Field = (value: string, field: "certificatePath" | "privateKeyPath") => {
    if (identity.kind !== "x509") return;
    onChangeIdentity({ ...identity, [field]: value });
  };

  const identityHelpRows: EnumHelpRow[] = [
    { value: "Anonymous", meaning: t("general.opcUaIdentityAnonymousDesc") },
    { value: "UserName", meaning: t("general.opcUaIdentityUserNameDesc") },
    { value: "X509", meaning: t("general.opcUaIdentityX509Desc") },
  ];

  return (
    <div className="container mt-3" style={{ width: "100%", maxWidth: "none" }}>
      <div className="row">
        <div className="col-12 mt-3">
          <span className="generalStyles__inputFlexRequired">*</span>
          <span className="generalStyles__inputLabel">
            Identity
            <Tooltip
              title={
                <EnumHelpTable
                  valueHeader={t("general.opcUaEnumTableValue")}
                  meaningHeader={t("general.opcUaEnumTableMeaning")}
                  rows={identityHelpRows}
                />
              }
              overlayStyle={{ maxWidth: 560 }}
            >
              <FontAwesomeIcon icon={faCircleInfo} className="generalStyles__mlFix" />
            </Tooltip>
          </span>
          <div style={{ marginTop: 8 }}>
            <Radio.Group
              value={identity.kind}
              onChange={(e) => onChangeIdentity(blankIdentityForKind(e.target.value))}
            >
              <Radio value="anonymous">Anonymous</Radio>
              <Radio value="username">UserName</Radio>
              <Radio value="x509">X509</Radio>
            </Radio.Group>
          </div>
        </div>
      </div>
      {identity.kind === "username" && (
        <div className="row">
          <div className="col-12 col-md-6 mt-3">
            <OpcUaTextField
              label="Username"
              value={identity.username}
              onChange={(value) => onChangeUsernameField(value, "username")}
              required
            />
          </div>
          <div className="col-12 col-md-6 mt-3">
            <OpcUaTextField
              label="Password"
              value={identity.password}
              onChange={(value) => onChangeUsernameField(value, "password")}
              required
            />
          </div>
        </div>
      )}
      {identity.kind === "x509" && (
        <div className="row">
          <div className="col-12 col-md-6 mt-3">
            <OpcUaTextField
              label="Certificate Path"
              value={identity.certificatePath}
              onChange={(value) => onChangeX509Field(value, "certificatePath")}
              required
            />
          </div>
          <div className="col-12 col-md-6 mt-3">
            <OpcUaTextField
              label="Private Key Path"
              value={identity.privateKeyPath}
              onChange={(value) => onChangeX509Field(value, "privateKeyPath")}
              required
            />
          </div>
        </div>
      )}
    </div>
  );
}
